"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { trackAddToCart, event } from "@/lib/analytics"

interface CartItem {
  id: string
  productId: string
  variantId?: string
  quantity: number
  price: number
  product?: any
  variant?: any
}

interface Cart {
  id: string
  items: CartItem[]
  userId?: string
  sessionId?: string
}

interface CartContextType {
  cart: Cart | null
  loading: boolean
  addToCart: (item: { productId: string; variantId?: string; quantity: number; price: number }) => Promise<void>
  updateItem: (itemId: string, quantity: number) => Promise<void>
  removeItem: (itemId: string) => Promise<void>
  clearCart: () => Promise<void>
  refreshCart: () => Promise<void>
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchCart = async () => {
    setLoading(true)
    try {
      const res = await fetch("/api/cart")
      if (res.ok) {
        const data = await res.json()
        setCart(data)
      } else {
        console.error('[Cart] Failed to fetch cart:', res.status)
      }
    } catch (err) {
      console.error('[Cart] Error fetching cart:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCart()
  }, [])

  const addToCart = async (item: { productId: string; variantId?: string; quantity: number; price: number }) => {
    setLoading(true)
    try {
      const res = await fetch("/api/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(item),
      })
      if (res.ok) {
        const data = await res.json()
        setCart(data)
        
        // Track add to cart event
        const productName = `Product ${item.productId}`
        const totalValue = item.price * item.quantity
        
        trackAddToCart(item.productId, productName, totalValue)
        
        event({
          action: 'add_to_cart',
          category: 'ecommerce',
          label: productName,
          value: totalValue
        })
      } else {
        const error = await res.json()
        console.error('[Cart] Failed to add to cart:', error)
      }
    } catch (err) {
      console.error('[Cart] Error adding to cart:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateItem = async (itemId: string, quantity: number) => {
    setLoading(true)
    try {
      const res = await fetch("/api/cart", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, quantity }),
      })
      if (res.ok) {
        await fetchCart()
      } else {
        const error = await res.json()
        console.error('[Cart] Failed to update item:', error)
      }
    } catch (err) {
      console.error('[Cart] Error updating item:', err)
    } finally {
      setLoading(false)
    }
  }

  const removeItem = async (itemId: string) => {
    setLoading(true)
    try {
      const res = await fetch("/api/cart", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId }),
      })
      if (res.ok) {
        await fetchCart()
      } else {
        const error = await res.json()
        console.error('[Cart] Failed to remove item:', error)
      }
    } catch (err) {
      console.error('[Cart] Error removing item:', err)
    } finally {
      setLoading(false)
    }
  }

  const clearCart = async () => {
    setLoading(true)
    try {
      const res = await fetch("/api/cart", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clear: true }),
      })
      if (res.ok) {
        await fetchCart()
      } else {
        const error = await res.json()
        console.error('[Cart] Failed to clear cart:', error)
      }
    } catch (err) {
      console.error('[Cart] Error clearing cart:', err)
    } finally {
      setLoading(false)
    }
  }

  const refreshCart = fetchCart

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateItem, removeItem, clearCart, refreshCart }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error("useCart must be used within a CartProvider")
  return ctx
} 